import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { UserPlus, User, Briefcase } from "lucide-react";

const LICENSE_TEXTS = {
  pt: {
    section: "Qual é o seu perfil?",
    particular_title: "Sou cliente final",
    particular_desc: "Preciso preencher formulário(s) para mim ou minha família. Um profissional revisa e libera o PDF.",
    comercial_title: "Sou profissional",
    comercial_desc: "Preencho formulários para outras pessoas. Preciso gerar PDFs sozinho (requer licença).",
  },
  es: {
    section: "¿Cuál es su perfil?",
    particular_title: "Soy cliente final",
    particular_desc: "Necesito llenar formulario(s) para mí o mi familia. Un profesional revisa y libera el PDF.",
    comercial_title: "Soy profesional",
    comercial_desc: "Lleno formularios para otras personas. Necesito generar PDFs por mí mismo (requiere licencia).",
  },
  en: {
    section: "What's your profile?",
    particular_title: "I'm an end client",
    particular_desc: "I need to fill form(s) for myself or my family. A professional reviews and releases the PDF.",
    comercial_title: "I'm a professional",
    comercial_desc: "I fill forms for other people. I need to generate PDFs myself (requires license).",
  },
};

export default function RegisterPage() {
  const { t, lang } = useLanguage();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [licenseType, setLicenseType] = useState("particular");
  const [submitting, setSubmitting] = useState(false);
  const L = LICENSE_TEXTS[lang] || LICENSE_TEXTS.pt;

  useEffect(() => {
    const t = searchParams.get("type");
    if (t === "comercial" || t === "particular") setLicenseType(t);
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    if (password.length < 8) {
      toast.error(t.auth.password_min);
      return;
    }
    setSubmitting(true);
    try {
      await register(email, password, name, licenseType, lang);
      toast.success(t.auth.welcome);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-6 py-16" data-testid="register-page">
      <div className="hairline rounded-lg bg-card p-8 shadow-2xl md:p-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--gold))]">
          {t.auth.overline_register}
        </div>
        <h1 className="font-display mt-3 text-4xl font-bold tracking-tight">
          {t.auth.register_title}
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {t.auth.register_subtitle}
        </p>

        <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
          {/* License type selection */}
          <div className="space-y-2">
            <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              {L.section}
            </Label>
            <div className="grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                data-testid="register-license-particular"
                onClick={() => setLicenseType("particular")}
                className={`flex flex-col items-start gap-2 rounded-md border p-4 text-left transition-all ${
                  licenseType === "particular"
                    ? "border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/10 shadow-md shadow-[hsl(var(--gold))]/10"
                    : "border-border bg-background hover:border-[hsl(var(--gold))]/50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <User className={`h-4 w-4 ${licenseType === "particular" ? "text-[hsl(var(--gold))]" : "text-muted-foreground"}`} />
                  <span className="font-semibold text-sm">{L.particular_title}</span>
                </div>
                <p className="text-[11px] leading-relaxed text-muted-foreground">{L.particular_desc}</p>
              </button>
              <button
                type="button"
                data-testid="register-license-comercial"
                onClick={() => setLicenseType("comercial")}
                className={`flex flex-col items-start gap-2 rounded-md border p-4 text-left transition-all ${
                  licenseType === "comercial"
                    ? "border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/10 shadow-md shadow-[hsl(var(--gold))]/10"
                    : "border-border bg-background hover:border-[hsl(var(--gold))]/50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Briefcase className={`h-4 w-4 ${licenseType === "comercial" ? "text-[hsl(var(--gold))]" : "text-muted-foreground"}`} />
                  <span className="font-semibold text-sm">{L.comercial_title}</span>
                </div>
                <p className="text-[11px] leading-relaxed text-muted-foreground">{L.comercial_desc}</p>
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="name"
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground"
            >
              {t.auth.name}
            </Label>
            <Input
              id="name"
              data-testid="register-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              className="h-12"
            />
          </div>
          <div className="space-y-2">
            <Label
              htmlFor="email"
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground"
            >
              {t.auth.email}
            </Label>
            <Input
              id="email"
              type="email"
              data-testid="register-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              className="h-12"
            />
          </div>
          <div className="space-y-2">
            <Label
              htmlFor="password"
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground"
            >
              {t.auth.password}
            </Label>
            <Input
              id="password"
              type="password"
              data-testid="register-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
              className="h-12"
            />
            <p className="text-[11px] text-muted-foreground">
              {t.auth.password_min}
            </p>
          </div>
          <Button
            type="submit"
            data-testid="register-submit-button"
            disabled={submitting}
            className="h-12 w-full rounded-md bg-[hsl(var(--gold))] font-semibold text-[hsl(var(--primary-foreground))] shadow-lg shadow-[hsl(var(--gold))]/20 hover:bg-[hsl(var(--gold-soft))]"
          >
            <UserPlus className="mr-2 h-4 w-4" />
            {submitting ? t.common.loading : t.auth.register_cta}
          </Button>
        </form>
        <p className="mt-6 text-center text-sm text-muted-foreground">
          {t.auth.has_account}{" "}
          <Link
            to="/login"
            data-testid="register-goto-login"
            className="font-medium text-[hsl(var(--primary))] underline-offset-4 hover:underline"
          >
            {t.auth.login_cta}
          </Link>
        </p>
      </div>
    </div>
  );
}
